<template>
  <div class="tags__wrapper">
    <div ref="triggerRef">
      <slot
        name="trigger"
        :has-selection="hasSelection"
        :is-open="showMultiSelect"
        :toggle="togglePanel"
      >
        <button
          type="button"
          class="tags__trigger-button"
          data-test="tags-button-open"
          aria-label="Toggle tags panel"
          :aria-expanded="showMultiSelect ? 'true' : 'false'"
          :aria-controls="panelId"
          @click="togglePanel"
        >
          <span class="far fa-tag" />
        </button>
      </slot>
    </div>

    <div
      v-show="showMultiSelect"
      :id="panelId"
      ref="panelRef"
      class="tags__panel"
      :class="positionClass"
    >
      <div class="tags__panel-header">
        <div class="tags__search-row">
          <input
            ref="searchInput"
            :value="searchValue"
            type="text"
            :placeholder="inputPlaceholder"
            class="tags__search-input"
            data-test="tags-input-search"
            @input="updateSearchValue"
          >
          <button
            v-if="showCreateButton"
            type="button"
            class="tags__create-button"
            data-test="tags-button-create"
            :disabled="isCreatingTag"
            @click="createTag"
          >
            Create Tag
          </button>
        </div>
        <div class="tags__panel-title">
          Select from existing tags:
        </div>
      </div>

      <div class="tags__panel-body">
        <div
          v-if="loading"
          class="tags__state"
        >
          Loading tags...
        </div>
        <div
          v-else-if="!allTags.length"
          class="tags__state"
        >
          No tags available.
        </div>
        <ul
          v-else
          class="tags__list"
        >
          <li
            v-for="tag in allTags"
            :key="getTagKey(tag)"
            class="tags__li"
          >
            <label class="tags__tag-label">
              <input
                type="checkbox"
                :checked="isTagSelected(tag)"
                class="tags__checkbox-input"
                :data-test="`tags-checkbox-${getTagName(tag)}`"
                @change="toggleTagSelection(tag, $event.target.checked)"
              >
              <div class="tags__checkbox">
                <span class="tags__checkbox-tick fa-solid fa-check" />
              </div>
              <span class="tags__tag-name">{{ getTagName(tag) }}</span>
            </label>
          </li>
        </ul>
      </div>

      <div
        v-if="feedback || manageTagsUrl"
        class="tags__panel-footer"
      >
        <p
          v-if="feedback"
          class="tags__feedback"
          :class="`tags__feedback--${feedbackType}`"
        >
          {{ feedback }}
        </p>
        <a
          v-if="manageTagsUrl"
          :href="manageTagsUrl"
          class="tags__manage-link"
          target="_blank"
          rel="noopener noreferrer"
        >
          Manage tags
          <span class="fas fa-external-link" />
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce, uniqueId } from 'lodash-es';

import TagsService from '../services/tags';

export default {
  name: 'Tags',

  props: {
    modelValue: {
      type: Array,
      default: () => [],
      validator: (tags) => tags.every((tag) => typeof tag === 'string'),
    },
    allowCreate: {
      type: Boolean,
      default: true,
    },
    assetType: {
      type: String,
      default: undefined,
    },
    position: {
      type: String,
      default: 'right',
      validator: (value) => ['left', 'right'].includes(value),
    },
    manageTagsUrl: {
      type: String,
      default: '',
    },
  },

  emits: ['open-change', 'update:modelValue', 'change'],

  data() {
    return {
      showMultiSelect: false,
      searchValue: '',
      allTags: [],
      loading: false,
      isCreatingTag: false,
      tagsRequestId: 0,
      feedback: '',
      feedbackType: null,
      panelId: uniqueId('tags-panel-'),
    };
  },

  computed: {
    selectedTags() {
      return this.modelValue || [];
    },

    hasSelection() {
      return this.selectedTags.length > 0;
    },

    selectedTagNameSet() {
      return new Set(this.selectedTags.filter(Boolean));
    },

    inputPlaceholder() {
      return this.allowCreate ? 'Search or add tag...' : 'Search tags...';
    },

    trimmedSearchValue() {
      return this.searchValue.trim();
    },

    hasExactSearchMatch() {
      const searchTerm = this.trimmedSearchValue;

      if (!searchTerm) {
        return false;
      }

      return this.allTags.some((tag) => this.getTagName(tag) === searchTerm);
    },

    showCreateButton() {
      return this.allowCreate
        && Boolean(this.trimmedSearchValue)
        && !this.loading
        && !this.hasExactSearchMatch;
    },

    positionClass() {
      return `tags__panel--${this.position}`;
    },
  },

  watch: {
    assetType() {
      if (!this.showMultiSelect) {
        return;
      }

      this.loadTags();
    },

    searchValue() {
      if (!this.showMultiSelect) {
        return;
      }

      this.debouncedLoadTags();
    },

    selectedTags() {
      this.allTags = this.orderTags(this.allTags);
    },

    showMultiSelect(newVal) {
      this.$emit('open-change', newVal);

      if (newVal) {
        this.loadTags();
        this.focusSearchInput();
        return;
      }

      this.tagsRequestId += 1;
      this.loading = false;
      this.debouncedLoadTags.cancel();
      this.resetPanelState();
    },
  },

  created() {
    this.debouncedLoadTags = debounce(this.loadTags, 250);
  },

  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },

  beforeUnmount() {
    this.debouncedLoadTags.cancel();
    document.removeEventListener('click', this.handleClickOutside);
  },

  methods: {
    updateSearchValue(event) {
      this.searchValue = event.target.value;
    },

    async loadTags() {
      const requestId = this.tagsRequestId + 1;
      this.tagsRequestId = requestId;

      try {
        this.loading = true;
        const response = await TagsService.getTags({
          assetType: this.assetType,
          search: this.searchValue || undefined,
        });

        if (requestId !== this.tagsRequestId) {
          return;
        }

        this.allTags = this.orderTags(response.tags || []);
        this.feedback = '';
        this.feedbackType = null;
      } catch (err) {
        if (requestId !== this.tagsRequestId) {
          return;
        }

        this.feedback = 'Failed to load tags.';
        this.feedbackType = 'error';
        console.error('Error loading tags:', err);
      } finally {
        if (requestId === this.tagsRequestId) {
          this.loading = false;
        }
      }
    },

    toggleTagSelection(tag, checked) {
      const tagName = this.getTagName(tag);

      if (!tagName) {
        return;
      }

      const isSelected = this.selectedTagNameSet.has(tagName);
      let full = [...this.selectedTags];
      let addedTags = [];
      let removedTags = [];

      if (checked && !isSelected) {
        full = [...this.selectedTags, tagName];
        addedTags = [tagName];
      } else if (!checked && isSelected) {
        full = this.selectedTags.filter((selectedTag) => selectedTag !== tagName);
        removedTags = [tagName];
      } else {
        return;
      }

      this.$emit('update:modelValue', full);
      this.$emit('change', {
        addedTags,
        removedTags,
        full,
      });
    },

    orderTags(tags) {
      const selected = [];
      const others = [];

      tags.forEach((tag) => {
        const tagName = this.getTagName(tag);

        if (this.selectedTagNameSet.has(tagName)) {
          selected.push(tag);
        } else {
          others.push(tag);
        }
      });

      return [...selected, ...others];
    },

    findTagByName(tagName) {
      return this.allTags.find((tag) => this.getTagName(tag) === tagName);
    },

    async createTag() {
      if (this.isCreatingTag) {
        return;
      }

      const raw = this.searchValue;
      if (!raw) {
        this.feedback = 'Type a tag name first.';
        this.feedbackType = 'error';
        return;
      }

      const newTag = this.trimmedSearchValue.replace(/\s+/g, ' ');
      if (!newTag) {
        this.searchValue = '';
        this.feedback = 'Tag cannot be empty.';
        this.feedbackType = 'error';
        return;
      }

      const existingTag = this.findTagByName(newTag);
      const wasExistingTag = Boolean(existingTag);

      this.isCreatingTag = true;

      try {
        const savedTag = await TagsService.createTag(newTag);
        const canonicalTag = this.findTagByName(this.getTagName(savedTag)) || savedTag;
        const canonicalTagName = this.getTagName(canonicalTag);

        if (!this.findTagByName(canonicalTagName)) {
          this.allTags = this.orderTags([...this.allTags, canonicalTag]);
        }

        if (this.isTagSelected(canonicalTag)) {
          this.feedback = 'That tag is already selected.';
          this.feedbackType = 'info';
        } else {
          this.toggleTagSelection(canonicalTag, true);
          this.feedback = wasExistingTag ? 'Existing tag selected.' : 'Tag added.';
          this.feedbackType = 'success';
        }
      } catch (err) {
        this.feedback = err.message || 'Failed to add tag.';
        this.feedbackType = 'error';
        return;
      } finally {
        this.isCreatingTag = false;
      }

      this.searchValue = '';
    },

    togglePanel() {
      this.showMultiSelect = !this.showMultiSelect;
    },

    resetPanelState() {
      this.searchValue = '';
      this.feedback = '';
      this.feedbackType = null;
    },

    focusSearchInput() {
      this.$nextTick(() => {
        this.$refs.searchInput?.focus();
      });
    },

    getTagName(tag) {
      return typeof tag === 'string' ? tag : tag?.tagName || '';
    },

    getTagKey(tag) {
      if (typeof tag === 'string') {
        return tag;
      }

      return tag?.id || tag?._id || this.getTagName(tag);
    },

    isTagSelected(tag) {
      const tagName = this.getTagName(tag);

      return this.selectedTagNameSet.has(tagName);
    },

    handleClickOutside(event) {
      const { panelRef, triggerRef } = this.$refs;

      if (
        this.showMultiSelect
        && panelRef
        && !panelRef.contains(event.target)
        && (!triggerRef || !triggerRef.contains(event.target))
      ) {
        this.showMultiSelect = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.tags {
  &__wrapper {
  position: relative;
  }

  &__panel {
    position: absolute;
    background-color: white;
    top: calc(100% + 8px);
    left: 0;
    width: 448px;
    max-width: min(448px, calc(100vw - 32px));
    z-index: 99;
    border: 1px solid #d9e2ec;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
    border-radius: 12px;
    overflow: hidden;
  }

  &__panel--left {
    left: auto;
    right: 0;
  }

  &__panel-header {
    padding: 16px;
    border-bottom: 1px solid #e6edf3;
    background: #fff;
  }

  &__panel-title {
    margin-top: 14px;
    font-size: 14px;
    font-weight: 500;
    color: #3e4c59;
  }

  &__search-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__search-input {
    width: 100%;
    height: 36px;
    min-width: 0;
    padding: 0 14px;
    border: 1px solid #d6dee6;
    border-radius: 6px;
    font-size: 14px;
    color: #243b53;

    &::placeholder {
      color: #7b8794;
    }

    &:focus {
      outline: none;
      border-color: #7ecae6;
      box-shadow: 0 0 0 3px rgba(0, 180, 204, 0.12);
    }
  }

  &__create-button {
    height: 36px;
    flex-shrink: 0;
    padding: 0 12px;
    border: 1px solid #00b4cc;
    border-radius: 6px;
    background: #fff;
    color: #00838f;
    font-size: 13px;
    font-weight: 600;
    line-height: 1;

    &:hover,
    &:focus {
      background: #e0f7fa;
      outline: none;
      text-decoration: none;
    }
  }

  &__panel-body {
    background: #fff;
  }

  &__state {
    padding: 20px 16px;
    color: #7b8794;
    font-size: 14px;
  }

  &__list {
    list-style: none;
    margin: 0;
    padding: 0;
    max-height: 236px;
    overflow: auto;
  }

  &__li {
    padding: 0 16px;
  }

  &__tag-label {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    margin: 0;
    padding: 10px 0;
    cursor: pointer;
    font-weight: 400;
    color: #102a43;
  }

  &__checkbox-input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }

  &__checkbox {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    border: 1px solid #d6dee6;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: transparent;
    transition: all 0.15s ease;
    background: #fff;
  }

  &__checkbox-tick {
    font-size: 10px;
  }

  &__checkbox-input:checked + &__checkbox {
    border-color: #00b4cc;
    background: #e0f7fa;
    color: #00838f;
  }

  &__tag-name {
    flex: 1;
    font-size: 14px;
    line-height: 1.3;
  }

  &__panel-footer {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 14px 16px;
    border-top: 1px solid #e6edf3;
    background: #fff;
  }

  &__manage-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #00a3bf;
    font-size: 14px;
    font-weight: 500;
    text-decoration: none;

    &:hover,
    &:focus {
      color: #00879f;
      text-decoration: none;
    }
  }

  &__feedback {
    margin: 0;
    font-size: 12px;
    line-height: 1.4;

    &--success {
      color: #2e7d32;
    }

    &--info {
      color: #1565c0;
    }

    &--error {
      color: #c62828;
    }
  }
}

</style>
